import Vue from 'vue'
import Router from 'vue-router'
import Dashboard from '@/views/Dashboard.vue'
import Login from '@/views/auth/Login.vue'
import { TokenService } from '@/services/storage.service'
import PlayerRoutes from './players'
import ApplicationWindows from './applicationWindows'
import CoachRoutes from './coaches'
import ClubRoutes from './clubs'
import LeagueRoutes from './leagues'
import SeasonRoutes from './seasons'
import VenueRoutes from './venues'
import NewsRoutes from './news'
import ScheduleRoutes from './schedules'
import SettingsRoutes from './settings'
import ImageRequestRoutes from './imageRequests'
import MemberRoutes from './members'
import DocumentRoutes from './documents'
import SponsorRoutes from './sponsors'
import ActivityRoutes from './activities'
import store from '@/store'
import Default from '../layouts/Default'
import Auth from '../layouts/Auth'
import PenaltyRoutes from '@/router/penalties'
import Resolve from '@/views/Resolve.vue'
import BillingRoutes from './billing'
import LivestreamRoutes from './livestream'
import ProductEnablementRoutes from './productEnablement'
import QrCodeRoutes from './qrCode'
import FantasyRoutes from './fantasy'
import VotingFormRoutes from './votingForms'

Vue.use(Router)

// A duplicated, redirected, cancelled or aborted navigation is expected (e.g. several
// logouts pushing the login route at once) and must not surface as an unhandled rejection.
const { isNavigationFailure, NavigationFailureType } = Router
const expectedNavigationFailures = [
  NavigationFailureType.duplicated,
  NavigationFailureType.redirected,
  NavigationFailureType.cancelled,
  NavigationFailureType.aborted
]

;['push', 'replace'].forEach(method => {
  const original = Router.prototype[method]

  Router.prototype[method] = function (location, onComplete, onAbort) {
    if (onComplete || onAbort) {
      return original.call(this, location, onComplete, onAbort)
    }

    return original.call(this, location).catch(error => {
      if (expectedNavigationFailures.some(type => isNavigationFailure(error, type))) {
        return error
      }

      throw error
    })
  }
})

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/auth',
      name: 'auth',
      component: Auth,
      children: [
        {
          path: 'login',
          name: 'login',
          component: Login,
          meta: {
            public: true,
            onlyWhenLoggedOut: true
          }
        }
      ]
    },
    {
      path: '',
      name: 'admin',
      component: Default,
      beforeRouteUpdate: () => {
        store.commit('loaded')
      },
      children: [
        {
          path: '/',
          name: 'resolve',
          component: Resolve
        },
        {
          path: ':clientId/dashboard',
          name: 'dashboard',
          component: Dashboard
        },
        {
          path: ':clientId/gallery',
          name: 'gallery.index',
          component: () => import('@/views/Gallery.vue'),
          meta: { featureKey: 'gallery' }
        },
        ...PlayerRoutes,
        ...CoachRoutes,
        ...ClubRoutes,
        ...LeagueRoutes,
        ...SeasonRoutes,
        ...VenueRoutes,
        ...MemberRoutes,
        ...ScheduleRoutes,
        ...NewsRoutes,
        ...SettingsRoutes,
        ...ImageRequestRoutes,
        ...DocumentRoutes,
        ...SponsorRoutes,
        ...PenaltyRoutes,
        ...ActivityRoutes,
        ...ApplicationWindows,
        ...BillingRoutes,
        ...LivestreamRoutes,
        ...ProductEnablementRoutes,
        ...QrCodeRoutes,
        ...FantasyRoutes,
        ...VotingFormRoutes
      ]
    }
  ],
  scrollBehavior (to, from, savedPosition) {
    if (to.path === from.path) {
      return false
    }

    const layoutId = document.getElementById('page-container')
    if (layoutId) {
      layoutId.scrollTo(0, 0)
    }

    return false
  }
})

router.beforeEach(async (to, from, next) => {
  // await store.dispatch('setClient', value)
  // eslint-disable-next-line
  jQuery('.modal').modal('hide')
  store.commit('loading')
  const isPublic = to.matched.some(record => record.meta.public)
  const onlyWhenLoggedOut = to.matched.some(record => record.meta.onlyWhenLoggedOut)
  const loggedIn = !!TokenService.getToken()
  if (!isPublic && !loggedIn) {
    return next({
      name: 'login',
      query: { redirect: to.fullPath }
      // Store the full path to redirect the user to after login
    })
  }

  // Do not allow user to visit login page or register page if they are logged in
  if (loggedIn && onlyWhenLoggedOut) {
    return next('/')
  }

  next()
})

router.beforeResolve((to, from, next) => {
  const featureKey = to.matched.find(r => r.meta.featureKey)?.meta.featureKey
  if (featureKey && store.getters.disabledFeatures.includes(featureKey)) {
    return next({ name: 'dashboard', params: { clientId: to.params.clientId } })
  }
  next()
})

router.afterEach((to, from) => {
  Vue.prototype.$mixpanel.track('Page Viewed', {
    path: to.path,
    from: from.path
  })
  // eslint-disable-next-line
  store.dispatch('closeNav')
  store.commit('loaded')
})

export default router
